import * as React from "react";
import { cn } from "@/lib/utils";
import { useCalculatorTheme } from "@/contexts/CalculatorThemeContext";

export interface CalculatorInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  prefix?: string;
  suffix?: string;
}

const CalculatorInput = React.forwardRef<HTMLInputElement, CalculatorInputProps>(
  ({ className, type, prefix, suffix, ...props }, ref) => {
    // Try to use theme context, fallback to dark if not available
    let isDark = true;
    try {
      const theme = useCalculatorTheme();
      isDark = theme.isDark;
    } catch {
      // Component is used outside of CalculatorThemeProvider, default to dark
    }

    const baseStyles = isDark
      ? "bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50"
      : "bg-white/80 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-navy/50";

    return (
      <div className="relative">
        {prefix && (
          <span 
            className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-300",
              isDark ? "text-white/40" : "text-slate-500"
            )}
          >
            {prefix}
          </span>
        )}
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border px-3 py-2 text-base ring-offset-background transition-all duration-300 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            baseStyles,
            prefix && "pl-7",
            suffix && "pr-7",
            className,
          )}
          ref={ref}
          {...props}
        />
        {suffix && (
          <span 
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-300",
              isDark ? "text-white/40" : "text-slate-500"
            )}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  },
);
CalculatorInput.displayName = "CalculatorInput";

// Currency Input Wrapper
interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  min?: number;
}

export const CalculatorCurrencyInput = ({
  value,
  onChange,
  placeholder = "0",
  id,
  className = "",
  min = 0,
}: CurrencyInputProps) => (
  <CalculatorInput
    id={id}
    type="number"
    value={value || ""}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    placeholder={placeholder}
    prefix="$"
    min={min}
    className={className}
  />
);

// Percent Input Wrapper
interface PercentInputProps {
  value: number;
  onChange: (value: number) => void;
  id?: string;
  min?: number;
  max?: number;
  step?: number;
}

export const CalculatorPercentInput = ({
  value,
  onChange,
  id,
  min = 0,
  max = 100,
  step = 0.1,
}: PercentInputProps) => (
  <CalculatorInput
    id={id}
    type="number"
    value={value || ""}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    placeholder="0"
    suffix="%"
    min={min}
    max={max}
    step={step}
  />
);

export { CalculatorInput };
